import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { personalInboxFiltersAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const emptyForm = { subject: '', match_type: 'prefix', active: true };

const matchTypeLabel = (matchType) => (matchType === 'exact' ? 'Exact' : 'Prefix');

const AdminPersonalInboxFiltersPage = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState([]);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [dialogError, setDialogError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await personalInboxFiltersAPI.getAll();
      setFilters(toArray(res.data, 'personal inbox filters'));
    } catch (err) {
      console.error('Error loading personal inbox filters:', err);
      setError(err.response?.data?.error || 'Failed to load personal inbox filters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenAdd = () => {
    setSelectedFilter(null);
    setFormData(emptyForm);
    setDialogError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (filterRow) => {
    setSelectedFilter(filterRow);
    setFormData({
      subject: filterRow.subject,
      match_type: filterRow.match_type,
      active: filterRow.active,
    });
    setDialogError('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedFilter(null);
    setFormData(emptyForm);
    setDialogError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const subject = formData.subject.trim();
    if (!subject) {
      setDialogError('Subject is required');
      return;
    }

    setSaving(true);
    setDialogError('');
    try {
      if (selectedFilter) {
        await personalInboxFiltersAPI.update(selectedFilter.id, {
          subject,
          match_type: formData.match_type,
          active: formData.active,
        });
      } else {
        await personalInboxFiltersAPI.create({
          subject,
          match_type: formData.match_type,
          active: formData.active,
        });
      }
      handleClose();
      loadData();
    } catch (err) {
      console.error('Error saving personal inbox filter:', err);
      setDialogError(err.response?.data?.error || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (filterRow) => {
    try {
      await personalInboxFiltersAPI.update(filterRow.id, { active: !filterRow.active });
      loadData();
    } catch (err) {
      console.error('Error toggling personal inbox filter:', err);
      notify.error(errorMessage(err, 'Failed to update filter'));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await personalInboxFiltersAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      console.error('Error deleting personal inbox filter:', err);
      notify.error(errorMessage(err, 'Failed to delete filter'));
    }
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Personal Inbox Filters</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Filter
            </Box>
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: error ? 1 : 2 }}>
          The personal-inbox receipt scanner only acts on unseen emails whose subject
          matches an active filter below — everything else in the inbox is left
          untouched. A match is scanned the same way as an emailed receipt and shows
          up for review on the Expenses page.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Match Type</TableCell>
                <TableCell>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={4} />}
              {filters.map((filterRow) => (
                <TableRow key={filterRow.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(filterRow) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => setDeleteTarget(filterRow), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{filterRow.subject}</TableCell>
                  <TableCell>{matchTypeLabel(filterRow.match_type)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!filterRow.active}
                      onChange={() => handleToggleActive(filterRow)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filters.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No personal inbox filters found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedFilter ? 'Edit Filter' : 'Add Filter'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField
              size="small"
              name="subject"
              label="Subject"
              value={formData.subject}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Your Mudflap receipt for"
            />
            <TextField
              size="small"
              name="match_type"
              label="Match Type"
              select
              value={formData.match_type}
              onChange={handleChange}
              fullWidth
              helperText={
                formData.match_type === 'exact'
                  ? 'Subject must equal this text exactly.'
                  : 'Subject must start with this text.'
              }
            >
              <MenuItem value="prefix">Prefix</MenuItem>
              <MenuItem value="exact">Exact</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Filter"
        message={`Permanently delete the filter "${deleteTarget?.subject}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        confirmLabel="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default AdminPersonalInboxFiltersPage;
